import { QuantityChangePreview, QuantityChangeStaleError, QuantityChangeTarget } from './quantityChangeTypes';
import { getAccessToken } from '../authentication/supabaseService';
import { OrderHistory, EmailProductData } from '../../stores/appOrderHistoryStore';
import { getISODay, startOfWeek, isSameDay, getISOWeek, getYear, getMonth, isSameMonth, startOfISOWeek, endOfISOWeek, format } from 'date-fns';
import { Platform } from '../../types/Platforms';

export async function fetchOrderEmails(): Promise<OrderHistory[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/emails/getEmails`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error('Failed to fetch emails');
  return res.json();
}

export async function fetchOrderHistoryByRestaurant(
  restaurantId: string
): Promise<OrderHistory[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/getAll/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch order history: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function fetchOrderHistoryForAllRestaurants(): Promise<OrderHistory[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/getAllForAllRestaurants`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch order history for all restaurants: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function fetchOrderHistoryByPlatformIds(
  platformIds: string[],
  startDate?: Date,
  endDate?: Date
): Promise<OrderHistory[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/getByPlatformIds`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    // startDate/endDate are optional — when omitted the backend returns the full history.
    body: JSON.stringify({
      platformIds,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch order history by platform IDs: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/**
 * Fetches the earliest/latest order_date for the given platforms.
 * Used by the dashboard progressive loader to know how far back to chunk.
 */
export async function fetchOrderHistoryDateBounds(
  platformIds: string[]
): Promise<{ minDate: Date | null; maxDate: Date | null }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/getDateBoundsByPlatformIds`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ platformIds }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch order history date bounds: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    minDate: data.minDate ? new Date(data.minDate) : null,
    maxDate: data.maxDate ? new Date(data.maxDate) : null,
  };
}

export interface OrderStats {
  todaysSpending: number;
  thisWeekSpending: number;
  averageWeekdaySpending: number;
  averageWeekSpending: number;
  monthSpending: number;
  averageMonthSpending: number;
  totalSpending: number;
}

export function computeOrderStats(orders: OrderHistory[]): OrderStats {
  const today = new Date();
  const isoToday = getISODay(today);
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });

  const todaysSpending = orders
    .filter(o => isSameDay(new Date(o.orderDate), today))
    .reduce((sum, o) => sum + o.total, 0);

  const thisWeekSpending = orders
    .filter(o => {
      const d = new Date(o.orderDate);
      return d >= weekStart && d <= today;
    })
    .reduce((sum, o) => sum + o.total, 0);

  const weekdayTotals = orders
    .filter(o => getISODay(new Date(o.orderDate)) === isoToday)
    .map(o => o.total);
  const averageWeekdaySpending = weekdayTotals.length
    ? weekdayTotals.reduce((a, b) => a + b, 0) / weekdayTotals.length
    : 0;

  const weekBuckets: Record<string, number> = {};
  orders.forEach(o => {
    const d = new Date(o.orderDate);
    const wk = `${getYear(d)}-W${getISOWeek(d)}`;
    const dayNo = getISODay(d);
    if (dayNo <= isoToday) {
      weekBuckets[wk] = (weekBuckets[wk] || 0) + o.total;
    }
  });
  const weekVals = Object.values(weekBuckets);
  const averageWeekSpending = weekVals.length
    ? weekVals.reduce((a, b) => a + b, 0) / weekVals.length
    : 0;

  const monthSpending = orders
    .filter(o => isSameMonth(new Date(o.orderDate), today))
    .reduce((sum, o) => sum + o.total, 0);

  const monthBuckets: Record<string, number> = {};
  orders.forEach(o => {
    const d = new Date(o.orderDate);
    const key = `${getYear(d)}-${getMonth(d)}`;
    monthBuckets[key] = (monthBuckets[key] || 0) + o.total;
  });
  const monthVals = Object.values(monthBuckets);
  const averageMonthSpending = monthVals.length
    ? monthVals.reduce((a, b) => a + b, 0) / monthVals.length
    : 0;

  const totalSpending = orders.reduce((sum, o) => sum + o.total, 0);

  return {
    todaysSpending,
    thisWeekSpending,
    averageWeekdaySpending,
    averageWeekSpending,
    monthSpending,
    averageMonthSpending,
    totalSpending,
  };
}

export type SupplierStats = Record<Platform, number>;

export function computeSupplierStats(orders: OrderHistory[]): SupplierStats {
  return orders.reduce((stats, order) => {
    const plat = order.platform;
    stats[plat] = (stats[plat] || 0) + order.total;
    return stats;
  }, {} as SupplierStats);
}

export function getWeekOrders(orders: OrderHistory[], weekStart: Date): OrderHistory[] {
  const weekEnd = endOfISOWeek(weekStart);
  return orders.filter(order => {
    const orderDate = new Date(order.orderDate);
    return orderDate >= weekStart && orderDate <= weekEnd;
  });
}

export function getWeekYear(date: Date): number {
  return getYear(date);
}

export function formatWeekRange(weekStart: Date): string {
  const weekEnd = endOfISOWeek(weekStart);
  const startFormat = format(weekStart, 'EEE d MMM');
  const endFormat = format(weekEnd, 'EEE d MMM yyyy');
  return `${startFormat} - ${endFormat}`;
}

export function getAvailableWeeks(orders: OrderHistory[]): Date[] {
  if (orders.length === 0) return [];

  const weeks = new Set<string>();
  orders.forEach(order => {
    const weekStart = startOfISOWeek(new Date(order.orderDate));
    weeks.add(weekStart.toISOString());
  });

  return Array.from(weeks)
    .map(dateStr => new Date(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());
}

export function getCombinedProductList(orders: OrderHistory[]): EmailProductData[] {
  type Acc = EmailProductData & {
    _quantitySum: number;
    _priceSum: number;
  };
  const productMap = new Map<string, Acc>();

  orders.forEach(order =>
    (order.productData || []).forEach(p => {
      const key = p.articleId || p.name;
      if (!productMap.has(key)) {
        productMap.set(key, {
          ...p,
          _quantitySum: p.quantity || 0,
          _priceSum: (p.price || 0) * (p.quantity || 0),
        });
      } else {
        const acc = productMap.get(key)!;
        acc._quantitySum += p.quantity || 0;
        acc._priceSum += (p.price || 0) * (p.quantity || 0);
        acc.quantity = acc._quantitySum;
      }
    })
  );

  return Array.from(productMap.values())
    .map(({ _quantitySum, _priceSum, ...p }) => ({
      ...p,
      quantity: _quantitySum,
      price: _quantitySum > 0 ? _priceSum / _quantitySum : 0,
    }))
    .sort((a, b) => (b.quantity * b.price) - (a.quantity * a.price));
}

export async function updateProductCategory(
  restaurantPlatformId: string,
  articleId: string | undefined,
  productName: string,
  mainCategory: string,
  category: string,
  subCategories: string[]
): Promise<{ success: boolean; updateCount: number; message: string }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/updateProductCategory`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      restaurantPlatformId,
      articleId,
      productName,
      mainCategory,
      category,
      subCategories,
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to update product category: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export async function updateProductQuantity(
  restaurantPlatformId: string,
  articleId: string | undefined,
  productName: string,
  newQuantity: number,
  startDate: Date,
  endDate: Date
): Promise<{ success: boolean; updateCount: number; message: string }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/updateProductQuantity`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      restaurantPlatformId,
      articleId,
      productName,
      newQuantity,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to update product quantity: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export async function updateProductQuantityInfo(
  restaurantPlatformId: string,
  platform: string,
  articleId: string | undefined,
  productName: string,
  override: { stdAmount: number; stdMultiplier: number; stdUnit: string; weightString: string },
  previewFingerprint?: string
): Promise<{ success: boolean; updateCount: number; message: string }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/updateProductQuantityInfo`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      restaurantPlatformId,
      platform,
      articleId,
      productName,
      ...override,
      previewFingerprint,
    }),
  });

  if (res.status === 409) throw new QuantityChangeStaleError();
  if (!res.ok) {
    throw new Error(`Failed to update product quantity info: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

/** What a quantity change would do, before doing it. `override: null` asks about a reset. */
export async function previewProductQuantityChange(
  restaurantPlatformId: string,
  platform: string,
  articleId: string | undefined,
  productName: string,
  override: QuantityChangeTarget | null
): Promise<QuantityChangePreview> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/quantity-change/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ restaurantPlatformId, platform, articleId, productName, ...(override ?? { reset: true }) }),
  });

  if (!res.ok) {
    throw new Error(`Failed to preview quantity change: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export async function resetProductQuantityInfo(
  restaurantPlatformId: string,
  platform: string,
  articleId: string | undefined,
  productName: string,
  previewFingerprint?: string
): Promise<{ success: boolean; updateCount: number; message: string }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/resetProductQuantityInfo`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      restaurantPlatformId,
      platform,
      articleId,
      productName,
      previewFingerprint,
    }),
  });

  if (res.status === 409) throw new QuantityChangeStaleError();
  if (!res.ok) {
    throw new Error(`Failed to reset product quantity info: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export async function updateProductName(
  restaurantPlatformId: string,
  articleId: string | undefined,
  oldProductName: string,
  newProductName: string,
  startDate: Date,
  endDate: Date
): Promise<{ success: boolean; updateCount: number; message: string }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/updateProductName`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      restaurantPlatformId,
      articleId,
      oldProductName,
      newProductName,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to update product name: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export async function updateProductPrice(
  restaurantPlatformId: string,
  articleId: string | undefined,
  productName: string,
  newPrice: number,
  startDate: Date,
  endDate: Date
): Promise<{ success: boolean; updateCount: number; message: string }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/updateProductPrice`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      restaurantPlatformId,
      articleId,
      productName,
      newPrice,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to update product price: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

export async function deleteOrder(orderId: string): Promise<{ success: boolean; message: string }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/orderhistory/deleteOrder/${orderId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to delete order: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

